import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ClubDashboardShortkeyButtonProps {
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const ClubDashboardShortkeyButton: React.FC<ClubDashboardShortkeyButtonProps> = ({
  label = 'باشگاه مشتریان',
  className = '',
  size = 'md',
}) => {
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    navigate(`/dashboard/club${window.location.search}`);
  };

  const handleMouseEnter = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 40,
        left: rect.left + rect.width / 2,
      });
    }
    timeoutRef.current = setTimeout(() => setShowTooltip(true), 400);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const sizeClasses = {
    sm: 'h-8 px-2 text-[11px] gap-1',
    md: 'h-10 px-2.5 text-xs gap-1.5',
    lg: 'h-12 px-3 text-sm gap-1.5',
  };

  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 18,
  };

  return (
    <>
      {showTooltip &&
        createPortal(
          <div
            className="fixed transform -translate-x-1/2 z-[99999] pointer-events-none"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            <div className="bg-gray-800 text-white text-sm px-3 py-2 rounded-md whitespace-nowrap shadow-md animate-fadeIn">
              رفتن به {label}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-800" />
              </div>
            </div>
          </div>,
          document.body,
        )}

      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
          inline-flex items-center justify-center shrink-0 rounded-full
          border border-border-primary bg-bg-secondary font-medium text-text-primary
          transition-all duration-200
          hover:bg-bg-tertiary hover:border-accent-purple/40 hover:shadow-md
          focus:outline-none focus:ring-2 focus:ring-accent-purple focus:ring-offset-1
          ${sizeClasses[size]}
          ${className}
        `}
        aria-label={`رفتن به ${label}`}
      >
        <Users
          size={iconSizes[size]}
          className="text-accent-purple shrink-0"
          strokeWidth={2}
          aria-hidden="true"
        />
        <span className="whitespace-nowrap">{label}</span>
      </button>
    </>
  );
};

export default ClubDashboardShortkeyButton;
